import axios from 'axios';
import { API_URL } from '../config';
import { uploadSizeError } from './uploadLimits';
import { prepareMediaForUpload } from './imageResize';

const UPLOADS_URL = `${API_URL}/api/uploads`;

// Header values must be printable ASCII; a passphrase that isn't only
// travels in the body (where the server also accepts it).
const isHeaderSafe = (value) => /^[\x20-\x7e]+$/.test(value);

// Auth headers the server checks before parsing a body: an admin bearer
// token, or the writer passphrase in X-Writer-Passphrase.
const buildAuthHeaders = ({ adminToken, passphrase }) => {
    if (adminToken) {
        return { Authorization: `Bearer ${adminToken}` };
    }
    return passphrase && isHeaderSafe(passphrase) ? { 'X-Writer-Passphrase': passphrase } : {};
};

export const RATE_LIMITED_MESSAGE = 'Too many uploads in a short time — please wait a few minutes and try again.';

// The server's JSON message when there is one; a rate-limit reply without
// one (older backends, or the proxy in front) still reads as a sentence
// instead of "Request failed with status code 429".
const serverMessage = (error, fallback) => {
    const message = error?.response?.data?.message;
    if (typeof message === 'string' && message.trim()) {
        return message;
    }
    if (error?.response?.status === 429) {
        return RATE_LIMITED_MESSAGE;
    }
    return fallback;
};

// Errors carry the HTTP status next to the readable message, so the editor
// can tell a refusal of one file (413, unsupported type) from one that will
// repeat for every file in a batch (403 wrong passphrase, 429 rate limited).
const toUploadError = (error, fallback) => {
    const uploadError = new Error(serverMessage(error, fallback));
    const status = error?.response?.status;
    if (status) {
        uploadError.status = status;
    }
    return uploadError;
};

// Presign answers that mean "use the multipart route for this file": older
// backends have no presign route (404), newer ones answer 503 until R2 is
// configured — and any other 503 (a proxy or tunnel hiccup, a restarting
// backend) is worth one multipart try too.
const directUploadUnavailable = (error) => {
    const status = error?.response?.status;
    return status === 404 || status === 503;
};

// Only the lasting answers are remembered: no presign route at all, or the
// backend's own "R2 is not configured" reply. A bare 503 is not, or one
// hiccup would send every later video to the 100MB multipart route.
const directUploadUnavailableForSession = (error) => {
    const status = error?.response?.status;
    return status === 404
        || (status === 503 && error?.response?.data?.code === 'DIRECT_UPLOAD_UNAVAILABLE');
};

// Remembered for the rest of the page session once presign says direct
// uploads are unavailable — R2 configuration does not change mid-session, so
// later files go straight to multipart and save one request each.
let skipDirectUploads = false;

// Test hook: forget the remembered answer.
export const resetDirectUploadAvailability = () => {
    skipDirectUploads = false;
};

// Multipart POST through the API (and Cloudflare's 100MB body limit) into
// GridFS. Resolves with the server-relative /api/images path so stored
// content survives host/protocol changes.
const uploadMultipart = async (file, { adminToken, passphrase, headers }) => {
    // Reject oversized files before the transfer starts; the server
    // enforces the same caps.
    const sizeError = uploadSizeError(file);
    if (sizeError) {
        throw new Error(sizeError);
    }

    const payload = new FormData();
    payload.append('image', file);
    if (!adminToken) {
        // Older backends only read the passphrase from the form body.
        payload.append('passphrase', passphrase);
    }

    try {
        const response = await axios.post(`${UPLOADS_URL}/images`, payload, {
            headers: { 'Content-Type': 'multipart/form-data', ...headers },
        });
        return response.data?.path || response.data?.url;
    } catch (error) {
        throw toUploadError(error, 'Failed to upload media');
    }
};

const requestPresign = async (file, { adminToken, passphrase, headers }) => {
    const response = await axios.post(`${UPLOADS_URL}/presign`, {
        filename: file.name,
        contentType: file.type,
        size: file.size,
        ...(adminToken ? {} : { passphrase }),
    }, { headers });

    const presigned = response.data || {};
    if (!presigned.key || !presigned.uploadUrl) {
        throw new Error('Failed to upload media');
    }
    return presigned;
};

// The PUT goes to R2 directly, so the bucket's CORS policy must allow the
// site origin (docs/R2-UPLOADS.md); a CORS failure surfaces here.
const putToStorage = async (file, presigned) => {
    try {
        await axios.put(presigned.uploadUrl, file, { headers: presigned.headers || {} });
    } catch {
        throw new Error('Upload to media storage failed');
    }
};

const completeUpload = async (key, { adminToken, passphrase, headers }) => {
    try {
        const response = await axios.post(`${UPLOADS_URL}/complete`, {
            key,
            ...(adminToken ? {} : { passphrase }),
        }, { headers });

        const url = response.data?.url;
        if (!url) {
            throw new Error('Failed to upload media');
        }
        return url;
    } catch (error) {
        throw toUploadError(error, 'Failed to upload media');
    }
};

/**
 * Uploads a media file for the editor. Photos are prepared first (HEIC
 * refused, large photos downscaled — utils/imageResize.js). Tries the
 * direct-to-R2 path first (presign → PUT to R2 → complete) and falls back to
 * the multipart route when the backend has no direct uploads. Resolves with
 * the URL (absolute for R2, server-relative for GridFS) to embed; rejects
 * with an Error whose message is safe to show to the writer (and whose
 * `status` is the HTTP status, when the server answered).
 */
export const uploadMediaFile = async (originalFile, { adminToken = '', passphrase = '' } = {}) => {
    const auth = {
        adminToken: adminToken.trim(),
        passphrase: passphrase.trim(),
    };
    auth.headers = buildAuthHeaders(auth);

    const file = await prepareMediaForUpload(originalFile);

    if (skipDirectUploads) {
        return uploadMultipart(file, auth);
    }

    let presigned;
    try {
        presigned = await requestPresign(file, auth);
    } catch (error) {
        if (directUploadUnavailable(error)) {
            if (directUploadUnavailableForSession(error)) {
                skipDirectUploads = true;
            }
            return uploadMultipart(file, auth);
        }
        throw toUploadError(error, error?.message || 'Failed to upload media');
    }

    await putToStorage(file, presigned);
    return completeUpload(presigned.key, auth);
};
